import { useState, useMemo, useCallback } from "react";

const PRESETS = [
  { id: "today", label: "Hoje" },
  { id: "7d", label: "Últimos 7 dias" },
  { id: "30d", label: "Últimos 30 dias" },
  { id: "90d", label: "Últimos 90 dias" },
  { id: "mtd", label: "Mês atual" },
  { id: "last_month", label: "Mês passado" },
  { id: "ytd", label: "Ano atual" },
  { id: "custom", label: "Personalizado" },
];

const COMPARE_OPTIONS = [
  { id: "previous_period", label: "Período anterior" },
  { id: "year_ago", label: "Mesmo período ano passado" },
  { id: "none", label: "Sem comparação" },
];

const fmt = (d) => d.toISOString().slice(0, 10);

const computePresetRange = (presetId) => {
  const today = new Date();
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (presetId) {
    case "today":
      return { dateStart: fmt(t), dateEnd: fmt(t) };
    case "7d":
      return { dateStart: fmt(new Date(t.getTime() - 6 * 86400000)), dateEnd: fmt(t) };
    case "30d":
      return { dateStart: fmt(new Date(t.getTime() - 29 * 86400000)), dateEnd: fmt(t) };
    case "90d":
      return { dateStart: fmt(new Date(t.getTime() - 89 * 86400000)), dateEnd: fmt(t) };
    case "mtd":
      return { dateStart: fmt(new Date(t.getFullYear(), t.getMonth(), 1)), dateEnd: fmt(t) };
    case "last_month": {
      const first = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const last = new Date(t.getFullYear(), t.getMonth(), 0);
      return { dateStart: fmt(first), dateEnd: fmt(last) };
    }
    case "ytd":
      return { dateStart: fmt(new Date(t.getFullYear(), 0, 1)), dateEnd: fmt(t) };
    default:
      return { dateStart: fmt(t), dateEnd: fmt(t) };
  }
};

/**
 * Estado dos filtros tem 2 camadas:
 *  - applied: o que está em uso pelos blocos (dispara fetch via useMemo)
 *  - draft:   o que o user está editando (apenas em modo "Personalizado")
 *
 * Presets aplicam imediatamente. Personalizado só aplica via "Buscar".
 */
export default function useInsightsFilters() {
  const initial = computePresetRange("30d");
  const [preset, setPreset] = useState("30d");

  // Camada applied — alimenta `filters` que vai pros blocos
  const [appliedDateStart, setAppliedDateStart] = useState(initial.dateStart);
  const [appliedDateEnd, setAppliedDateEnd] = useState(initial.dateEnd);
  const [compareWith, setCompareWith] = useState("previous_period");

  // Camada draft — só usada em modo custom
  const [draftDateStart, setDraftDateStart] = useState(initial.dateStart);
  const [draftDateEnd, setDraftDateEnd] = useState(initial.dateEnd);

  const applyPreset = useCallback((id) => {
    setPreset(id);
    if (id !== "custom") {
      const r = computePresetRange(id);
      setAppliedDateStart(r.dateStart);
      setAppliedDateEnd(r.dateEnd);
      setDraftDateStart(r.dateStart);
      setDraftDateEnd(r.dateEnd);
    }
    // se virou custom, não aplica nada — espera o usuário editar e clicar "Buscar"
  }, []);

  // Compara mudou: aplica imediatamente (como preset)
  const handleSetCompareWith = useCallback((v) => setCompareWith(v), []);

  // Botão "Buscar" no modo custom — promove draft → applied
  const applyCustomDates = useCallback(() => {
    if (!draftDateStart || !draftDateEnd) return;
    setAppliedDateStart(draftDateStart);
    setAppliedDateEnd(draftDateEnd);
  }, [draftDateStart, draftDateEnd]);

  const isDraftDirty =
    preset === "custom" &&
    (draftDateStart !== appliedDateStart || draftDateEnd !== appliedDateEnd);

  const filters = useMemo(
    () => ({
      dateStart: appliedDateStart,
      dateEnd: appliedDateEnd,
      compareWith,
    }),
    [appliedDateStart, appliedDateEnd, compareWith],
  );

  return {
    PRESETS,
    COMPARE_OPTIONS,
    preset,
    applyPreset,
    // Datas exibidas (draft em custom, applied em preset)
    dateStart: preset === "custom" ? draftDateStart : appliedDateStart,
    dateEnd: preset === "custom" ? draftDateEnd : appliedDateEnd,
    setDateStart: (v) => {
      setDraftDateStart(v);
      // Se user editar manualmente em modo não-custom, força "custom"
      if (preset !== "custom") setPreset("custom");
    },
    setDateEnd: (v) => {
      setDraftDateEnd(v);
      if (preset !== "custom") setPreset("custom");
    },
    compareWith,
    setCompareWith: handleSetCompareWith,
    applyCustomDates,
    isDraftDirty,
    filters,
  };
}
